import { AxiosResponse } from "axios";
import {
  SET_ANALYTIC_REPORTS,
  SET_ERRORMSG,
  SET_CONTACT_MOVEMENTS,
  SET_DASHBOARD_STATS,
  SET_CALL_CONNECT_RATE,
  SET_MEETING_BOOKING_METRICS,
} from "@/store/mutation-types";
import {
  CLEAR_REPORT_ISLOADING,
  GET_ANALYTIC_REPORTS,
  GET_CONTACT_MOVEMENTS,
  GET_DASHBOARD_STATS,
  GET_CALL_CONNECT_RATE,
  GET_MEETING_BOOKING_METRICS,
} from "@/store/action-types";
import { StateObject } from "@/dtos/State.dto";
import Report from "@/services/analytics/report";
import { Module } from "vuex";
import { GetReport } from "@/dtos/Report.dto";

const report: Module<any, {}> = {
  state: {
    dashboardStats: {
      data: null,
      isLoading: false,
    },
    analyticReports: {
      data: null,
      isLoading: false,
    },
    contactMovements: {
      data: null,
      isLoading: false,
    },
    // Unified endpoint returns all metrics in one response (for Call Connect Rate tab)
    callConnectRate: {
      data: null,
      isLoading: false,
    },
    // Meeting Booking Metrics tab has its own endpoint with different data structure
    meetingBookingMetrics: {
      data: null,
      isLoading: false,
    },
  },
  getters: {
    dashboardStats: (state: { dashboardStats: any }) => state.dashboardStats,
    analyticReports: (state: { analyticReports: any }) => state.analyticReports,
    contactMovements: (state: { contactMovements: any }) => state.contactMovements,
    callConnectRate: (state: { callConnectRate: any }) => state.callConnectRate,
    meetingBookingMetrics: (state: { meetingBookingMetrics: any }) => state.meetingBookingMetrics,
  },
  mutations: {
    [SET_DASHBOARD_STATS]: (
      state: { dashboardStats: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.dashboardStats.data = payload.data;
      state.dashboardStats.isLoading = payload.isLoading;
    },
    [SET_ANALYTIC_REPORTS]: (
      state: { analyticReports: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.analyticReports.data = payload.data;
      state.analyticReports.isLoading = payload.isLoading;
    },
    [SET_CONTACT_MOVEMENTS]: (
      state: { contactMovements: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.contactMovements.data = payload.data;
      state.contactMovements.isLoading = payload.isLoading;
    },
    [SET_CALL_CONNECT_RATE]: (
      state: { callConnectRate: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.callConnectRate.data = payload.data;
      state.callConnectRate.isLoading = payload.isLoading;
    },
    [SET_MEETING_BOOKING_METRICS]: (
      state: { meetingBookingMetrics: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      if (payload.data != null) state.meetingBookingMetrics.data = payload.data;
      state.meetingBookingMetrics.isLoading = payload.isLoading;
    },
  },
  actions: {
    [GET_DASHBOARD_STATS]: async ({ commit }: any, payload: any) => {
      commit(SET_DASHBOARD_STATS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Report.getDashboardStats(payload as GetReport);

        if (result) {
          if (result.data.status) {
            commit(SET_DASHBOARD_STATS, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_DASHBOARD_STATS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_DASHBOARD_STATS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_DASHBOARD_STATS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load dashboard statistics"
          );
        }
        return false;
      }
    },
    [GET_ANALYTIC_REPORTS]: async ({ commit }: any, payload: any) => {
      commit(SET_ANALYTIC_REPORTS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Report.getAnalyticReports(payload as GetReport);

        if (result) {
          if (result.data.status) {
            commit(SET_ANALYTIC_REPORTS, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_ANALYTIC_REPORTS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_ANALYTIC_REPORTS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_ANALYTIC_REPORTS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load analytic reports");
        }
        return false;
      }
    },
    [GET_CONTACT_MOVEMENTS]: async ({ commit }: any, payload: any) => {
      commit(SET_CONTACT_MOVEMENTS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Report.getContactMovements(payload as GetReport);

        if (result) {
          if (result.data.status) {
            commit(SET_CONTACT_MOVEMENTS, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_CONTACT_MOVEMENTS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CONTACT_MOVEMENTS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CONTACT_MOVEMENTS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(SET_ERRORMSG, error.response?.data?.message || "Failed to load contact movements");
        }
        return false;
      }
    },
    [GET_CALL_CONNECT_RATE]: async ({ commit }: any, payload: any) => {
      commit(SET_CALL_CONNECT_RATE, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Report.getCallConnectRate(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_CALL_CONNECT_RATE, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_CALL_CONNECT_RATE, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_CALL_CONNECT_RATE, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_CALL_CONNECT_RATE, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load call connect rate metrics"
          );
        }
        return false;
      }
    },
    [GET_MEETING_BOOKING_METRICS]: async ({ commit }: any, payload: any) => {
      commit(SET_MEETING_BOOKING_METRICS, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Report.getMeetingBookingMetrics(payload);

        if (result) {
          if (result.data.status) {
            commit(SET_MEETING_BOOKING_METRICS, {
              data: result.data.data,
              isLoading: false,
            });
            return true;
          }
          commit(SET_MEETING_BOOKING_METRICS, { data: null, isLoading: false });
          commit(SET_ERRORMSG, result.data.msg);
          return false;
        } else {
          commit(SET_MEETING_BOOKING_METRICS, { isLoading: false });
          return false;
        }
      } catch (error: any) {
        commit(SET_MEETING_BOOKING_METRICS, { data: null, isLoading: false });

        // Handle timeout specifically
        if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
          commit(SET_ERRORMSG, "Request timed out. Please check your connection and try again.");
        } else {
          commit(
            SET_ERRORMSG,
            error.response?.data?.message || "Failed to load meeting booking metrics"
          );
        }
        return false;
      }
    },
    [CLEAR_REPORT_ISLOADING]: ({ commit }) => {
      commit(SET_DASHBOARD_STATS, { isLoading: false });
      commit(SET_ANALYTIC_REPORTS, { isLoading: false });
      commit(SET_CONTACT_MOVEMENTS, { isLoading: false });
    },
  },
};

export default report;
